import { getNextFlowCount } from "./flow";
import type { MoveResolution } from "./helpers";
import type { BoardTile, FlowState, GameState, ScoreEvent, ScoreEventType } from "./types";

export const MATCH_CLEAR_POINTS = 10;
export const TEN_CLEAR_POINTS = 20;
export const ROUND_CLEAR_POINTS = 100;

function getBasePoints(type: ScoreEventType): number {
  if (type === "match_clear") {
    return MATCH_CLEAR_POINTS;
  }

  if (type === "ten_clear") {
    return TEN_CLEAR_POINTS;
  }

  return ROUND_CLEAR_POINTS;
}

function createScoreEvent(
  type: ScoreEventType,
  flowMultiplier: number,
  tileMultiplier: number,
  origin: ScoreEvent["origin"],
  boardIndex: number | null
): ScoreEvent {
  const basePoints = getBasePoints(type);
  const multiplier = flowMultiplier * tileMultiplier;

  return {
    type,
    basePoints,
    flowMultiplier,
    tileMultiplier,
    multiplier,
    awardedPoints: basePoints * multiplier,
    origin,
    boardIndex
  };
}

function getTileScoreMultiplier(clearedTile: BoardTile | null): number {
  if (clearedTile === null) {
    return 1;
  }

  return Math.max(1, clearedTile.buildDepth + 1);
}

export function getScoreMultiplier(flow: FlowState, playedAt: number): number {
  return Math.max(1, getNextFlowCount(flow, playedAt));
}

export function createScoreEvents(options: {
  moveOutcome: MoveResolution["outcome"];
  flow: FlowState;
  playedAt: number;
  boardIndex: number;
  roundCleared: boolean;
  clearedTile: BoardTile | null;
}): ScoreEvent[] {
  const flowMultiplier = getScoreMultiplier(options.flow, options.playedAt);
  const scoreEvents: ScoreEvent[] = [];

  if (options.moveOutcome === "match_clear" || options.moveOutcome === "negative_clear") {
    scoreEvents.push(
      createScoreEvent(
        "match_clear",
        flowMultiplier,
        getTileScoreMultiplier(options.clearedTile),
        "tile",
        options.boardIndex
      )
    );
  } else if (options.moveOutcome === "build_to_ten_clear") {
    scoreEvents.push(
      createScoreEvent(
        "ten_clear",
        flowMultiplier,
        getTileScoreMultiplier(options.clearedTile),
        "tile",
        options.boardIndex
      )
    );
  }

  if (options.roundCleared) {
    scoreEvents.push(createScoreEvent("round_clear", flowMultiplier, 1, "board_center", null));
  }

  return scoreEvents;
}

export function getAwardedPoints(scoreEvents: ScoreEvent[]): number {
  return scoreEvents.reduce((total, scoreEvent) => total + scoreEvent.awardedPoints, 0);
}

export function applyScoreEvents(state: GameState, scoreEvents: ScoreEvent[]): GameState {
  if (scoreEvents.length === 0) {
    return state;
  }

  return {
    ...state,
    score: state.score + getAwardedPoints(scoreEvents)
  };
}
